import Zone from "@insite/client-framework/Components/Zone";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import MobiusPage from "@insite/mobius/Page";
import * as React from "react";

const NewsPage: React.FunctionComponent<PageProps> = ({ id }) => (
    <MobiusPage>
        <Zone contentId={id} zoneName="Content" requireRows />
    </MobiusPage>
);

const pageModule: PageModule = {
    component: NewsPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        allowedParents: ["NewsListPage"],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            {
                ...HorizontalRule,
                sortOrder: 150,
            },
            {
                name: "trainingImage",
                editorTemplate: "ImagePickerField",
                fieldType: "General",
                defaultValue: "",
                sortOrder: 151,
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
            },
            {
                name: "trainingDate",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 152,
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
            },
            {
                name: "trainingEndDate",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: false,
                sortOrder: 154,
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
            },
            {
                fieldType: "General",
                name: "blogType",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "News List", value: "newsList" },
                    { displayName: "Crescent Parts Training", value: "crescentParts" },
                    { displayName: "Online Manufacturer Training", value: "onlineManufacturer" },
                ],
                defaultValue: "newsList",
                sortOrder: 156,
            },
            {
                name: "title",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 157,
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
            },
            {
                name: "trainingType",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 158,
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
            },
            {
                name: "description",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
                sortOrder: 159,
            },
            {
                name: "location",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.blogType === "crescentParts",
                sortOrder: 160,
            },
            {
                name: "price",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.blogType === "crescentParts",
                sortOrder: 161,
            },
            {
                name: "peu",
                displayName: "PEU",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.blogType === "crescentParts",
                sortOrder: 162,
            },
            {
                name: "restriction",
                displayName: "Restriction",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.blogType === "crescentParts",
                sortOrder: 163,
            },
            {
                name: "registrationLink",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.blogType === "onlineManufacturer",
                sortOrder: 164,
            },
            {
                fieldType: "General",
                name: "registrationType",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Required", value: "regRequired" },
                    { displayName: "Not Required", value: "regNotRequired" },
                    { displayName: "External", value: "regExternal" },
                ],
                defaultValue: "regRequired",
                sortOrder: 165,
            },
            {
                name: "buttonText",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 166,
                isVisible: widget =>
                    (widget.fields.registrationType === "regRequired" || widget.fields.registrationType === "regExternal") &&
                    (widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer"),
            },
            {
                name: "externalLink",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                sortOrder: 168,
                isVisible: widget =>
                    widget.fields.registrationType === "regExternal",
            },
            {
                name: "featured",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: widget =>
                    widget.fields.blogType === "crescentParts" || widget.fields.blogType === "onlineManufacturer",
                sortOrder: 169,
            },
            // Default news Page Fields
            {
                name: "newsContent",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                isRequired: true,
                sortOrder: 170,
                isVisible: widget => widget.fields.blogType === "newsList",
            },
            {
                name: "newsSummary",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                sortOrder: 171,
                isVisible: widget => widget.fields.blogType === "newsList",
            },
            {
                name: "publishDate",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 172,
                isVisible: widget => widget.fields.blogType === "newsList",
            },
            {
                name: "author",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 173,
                isVisible: widget => widget.fields.blogType === "newsList",
            },
            {
                name: "openInNewWindow",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 174,
            },
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "NewsPage" instead of this constant.
 */
export const NewsPageContext = "NewsPage";
